<script setup>
import PinAction from './header/PinAction.vue';
// SiteUp: pílula única da IA no header (orb + nome + ponto). Pause/retoma,
// troca de assistente, pin, reset e follow-up ficam no popover, mesmas
// actions/API de antes (ConversationAiAgentAPI.update).
import { computed, ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ConversationAiAgentAPI from 'dashboard/api/conversationAiAgent';
import ChipPopover from 'dashboard/components-next/Conversation/ContextChips/ChipPopover.vue';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';
import {
  resolveAiAgentMode,
  isAutonomousMode,
  hasActiveAiAgent,
} from 'dashboard/helper/aiAgentMode';
import { resolveAiPillState } from './header/aiPillState';

defineProps({
  compact: { type: Boolean, default: false },
  open: { type: Boolean, default: false },
  inboxName: { type: String, default: '' },
  // Modo trilho (V3): o gatilho vira um icone de 36px com ponto de estado, do
  // tamanho do trilho da direita. A pilula nao cabe nos 44px do trilho e
  // estourava por cima do painel de contatos. O popover e o mesmo.
  rail: { type: Boolean, default: false },
  // Acao de fixar no topo dentro do popover (V3): so onde o cabecalho pede.
  pinnable: { type: Boolean, default: false },
});

const emit = defineEmits(['toggle', 'close']);

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const assistants = useMapGetter('captainAssistants/getRecords');
const accountId = useMapGetter('getCurrentAccountId');

const active = ref(null);
const pinned = ref(false);
const saving = ref(false);
const followupState = ref(null);

const conversationId = computed(() => currentChat.value?.id);
const hasAgent = computed(() => hasActiveAiAgent(currentChat.value));
const activeName = computed(
  () => active.value?.name || t('CONVERSATION.HEADER.AI_FALLBACK_NAME')
);

const settings = computed(
  () => currentChat.value?.additional_attributes?.ai_agent || {}
);
const currentMode = computed(() => resolveAiAgentMode(currentChat.value));
const paused = computed(() => settings.value.paused === true);
const isAutonomous = computed(() => isAutonomousMode(currentMode.value));
const isSuggesting = computed(
  () =>
    currentMode.value === 'suggest_auto' ||
    currentMode.value === 'suggest_manual'
);
const canControlAi = computed(() => isAutonomous.value || isSuggesting.value);
const pillState = computed(() =>
  resolveAiPillState({
    hasAgent: hasAgent.value,
    canControl: canControlAi.value,
    paused: paused.value,
  })
);
const aiPaused = computed(() => pillState.value === 'paused');
const brasaState = computed(() => (aiPaused.value ? 'sleep' : 'hover'));
const showStatus = computed(() => canControlAi.value);
// Copy por MODO (Lucas, 02/09): em sugestao a IA nunca respondeu, entao
// "pausar/retomar a IA" mente. O verbo descreve o efeito real: parar/voltar
// a sugerir. O gate (paused) e o mesmo.
// Copy do popover e da pílula por modo real da conversa. Assistente só de
// sugestão (Lia) não "responde" nem se "pausa": ela rascunha e quem envia é a
// pessoa. Tabela em vez de ternário aninhado para a leitura não virar charada.
const COPIA_POR_MODO = {
  responde: {
    ativa: {
      status: 'AI_STATUS_ACTIVE',
      detalhe: null,
      botao: 'PAUSE_AI_FULL',
      dica: 'AI_PAUSE_HINT',
      pilula: 'AI_REPLYING',
      tooltip: 'AI_PILL_ACTIVE_TIP',
    },
    pausada: {
      status: 'AI_STATUS_PAUSED',
      detalhe: null,
      botao: 'RESUME_AI',
      dica: 'AI_PAUSE_HINT',
      pilula: 'AI_PAUSED',
      tooltip: 'AI_PILL_PAUSED_TIP',
    },
  },
  sugere: {
    ativa: {
      status: 'AI_STATUS_SUGGESTING',
      detalhe: 'AI_SUGGEST_DETAIL',
      botao: 'AI_SUGGEST_STOP',
      dica: 'AI_SUGGEST_PAUSE_HINT',
      pilula: 'AI_SUGGESTING_SHORT',
      tooltip: 'AI_PILL_SUGGESTING_TIP',
    },
    pausada: {
      status: 'AI_STATUS_SUGGEST_PAUSED',
      detalhe: 'AI_SUGGEST_DETAIL',
      botao: 'AI_SUGGEST_RESUME',
      dica: 'AI_SUGGEST_PAUSE_HINT',
      pilula: 'AI_SUGGEST_PAUSED_SHORT',
      tooltip: 'AI_PILL_SUGGEST_PAUSED_TIP',
    },
  },
};
const copiaPorModo = computed(
  () =>
    COPIA_POR_MODO[isSuggesting.value ? 'sugere' : 'responde'][
      aiPaused.value ? 'pausada' : 'ativa'
    ]
);
const followupEnabled = computed(() => followupState.value?.enabled === true);
const followupPaused = computed(() => followupState.value?.paused === true);
const followupCount = computed(() => followupState.value?.count || 0);
const showFollowup = computed(
  () => followupEnabled.value && showStatus.value && isAutonomous.value
);

const syncFromChat = () => {
  active.value = currentChat.value?.ai_agent_active_assistant || null;
  pinned.value = currentChat.value?.ai_agent_pinned === true;
  followupState.value = currentChat.value?.ai_agent_followup || null;
};
watch(() => currentChat.value?.id, syncFromChat, { immediate: true });
watch(
  () => currentChat.value?.ai_agent_followup,
  value => {
    followupState.value = value || null;
  }
);
watch(
  () => currentChat.value?.ai_agent_active_assistant,
  value => {
    if (value) active.value = value;
  }
);
watch(
  () => currentChat.value?.ai_agent_pinned,
  value => {
    pinned.value = value === true;
  }
);

onMounted(() => {
  if (!assistants.value?.length) {
    store.dispatch('captainAssistants/get').catch(() => {});
  }
});

const applyAiAgentState = state => {
  if (!state || !currentChat.value) return;

  const additionalAttributes = {
    ...(currentChat.value.additional_attributes || {}),
    ai_agent: {
      ...(currentChat.value.additional_attributes?.ai_agent || {}),
      active: state.active,
      mode: state.mode,
      paused: state.paused,
      pinned: state.pinned,
    },
  };

  store.dispatch('updateConversation', {
    ...currentChat.value,
    status: state.conversation_status || currentChat.value.status,
    updated_at: state.conversation_updated_at || new Date().toISOString(),
    additional_attributes: additionalAttributes,
    ai_agent_mode: state.mode,
    ai_agent_active: state.active,
    ai_agent_paused: state.paused,
    ai_agent_pinned: state.pinned,
    ai_agent_active_assistant: state.active_assistant,
    ai_agent_followup: state.followup,
  });

  if (state.active_assistant) active.value = state.active_assistant;
  pinned.value = state.pinned === true;
  followupState.value = state.followup || null;
};

const persist = async data => {
  if (!conversationId.value || saving.value) return null;
  saving.value = true;
  try {
    const { data: state } = await ConversationAiAgentAPI.update(
      conversationId.value,
      data
    );
    applyAiAgentState(state);
    return state;
  } catch (error) {
    useAlert(t('CONVERSATION.HEADER.AI_UPDATE_FAILED'));
    return null;
  } finally {
    saving.value = false;
  }
};

const toggleAi = async () => {
  // keep_status: sem ele o backend RESOLVE a conversa ao retomar (o chip do
  // cabecalho ja mandava; o popover nao). Em sugestao isso era claramente errado.
  await persist({ paused: !aiPaused.value, keep_status: true });
};

const setFollowupPaused = async value => {
  const state = await persist({ followup_paused: value });
  if (state?.followup) followupState.value = state.followup;
};

const configureAssistant = () => {
  emit('close');
  if (!active.value?.id) return;
  router.push({
    name: 'captain_assistants_settings_index',
    params: {
      accountId: accountId.value,
      assistantId: active.value.id,
    },
  });
};
</script>

<template>
  <!-- SiteUp: pílula da IA no desktop: ponto + rótulo + pausar dentro da pílula. -->
  <div class="contents">
    <div
      v-if="hasAgent"
      data-testid="header-ai-pill"
      :class="
        rail
          ? 'inline-flex'
          : [
              'inline-flex h-8 items-center gap-1 rounded-full border px-2 disabled:opacity-50',
              aiPaused
                ? 'border-n-amber-9/40 bg-n-amber-9/15 text-n-amber-11'
                : 'border-n-teal-9/40 bg-n-teal-9/15 text-n-teal-11',
            ]
      "
    >
      <ChipPopover :open="open" width-class="w-72" @close="emit('close')">
        <template #trigger>
          <button
            v-if="rail"
            type="button"
            class="relative flex size-9 items-center justify-center rounded-xl hover:bg-n-alpha-2"
            :class="{ 'bg-n-alpha-2': open }"
            :title="
              aiPaused
                ? t(`CONVERSATION.HEADER.${copiaPorModo.tooltip}`)
                : t(`CONVERSATION.HEADER.${copiaPorModo.tooltip}`)
            "
            @click="emit('toggle')"
          >
            <BrasaMascot
              :size="28"
              :state="brasaState"
              :animated="aiPaused ? false : null"
              :class="aiPaused ? 'grayscale opacity-55' : ''"
            />
            <span
              v-if="aiPaused"
              class="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-n-amber-9 ring-2 ring-n-surface-1"
              aria-hidden="true"
            >
              <span class="i-ri-pause-fill size-2.5 text-white" />
            </span>
          </button>
          <button
            v-else
            type="button"
            class="inline-flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-1 hover:opacity-90 disabled:opacity-50"
            :disabled="saving"
            :title="
              aiPaused
                ? t(`CONVERSATION.HEADER.${copiaPorModo.tooltip}`)
                : t(`CONVERSATION.HEADER.${copiaPorModo.tooltip}`)
            "
            @click="emit('toggle')"
          >
            <span
              class="size-2 shrink-0 rounded-full"
              :class="[
                aiPaused
                  ? 'bg-n-amber-9'
                  : 'bg-n-teal-9 motion-safe:animate-pulse',
              ]"
              aria-hidden="true"
            />
            <span v-if="!compact" class="text-[12.5px] font-bold">
              {{
                aiPaused
                  ? t(`CONVERSATION.HEADER.${copiaPorModo.pilula}`)
                  : t(`CONVERSATION.HEADER.${copiaPorModo.pilula}`)
              }}
            </span>
          </button>
        </template>

        <div class="flex items-center gap-3">
          <span
            class="relative flex size-10 shrink-0 items-center justify-center"
          >
            <BrasaMascot
              :size="40"
              :state="brasaState"
              :animated="aiPaused ? false : null"
              :class="aiPaused ? 'grayscale opacity-55' : ''"
            />
            <span
              v-if="aiPaused"
              class="i-ri-pause-fill pointer-events-none absolute size-4 text-white"
              aria-hidden="true"
            />
          </span>
          <div class="min-w-0 leading-tight">
            <b class="block truncate text-[14px] font-medium text-n-slate-12">
              {{ activeName }}
            </b>
            <span class="text-[11.5px] text-n-slate-11">
              {{
                t('CONVERSATION.HEADER.AI_ASSISTANT_SCOPE', {
                  scope: inboxName || t('CONVERSATION.HEADER.AI_FALLBACK_NAME'),
                })
              }}
            </span>
          </div>
        </div>

        <div
          v-if="showStatus"
          class="mt-3 flex items-center gap-2 rounded-xl bg-n-solid-2 px-3 py-2"
        >
          <span
            class="size-2 shrink-0 rounded-full"
            :class="aiPaused ? 'bg-n-amber-9' : 'bg-n-teal-9'"
            aria-hidden="true"
          />
          <span class="text-[12.5px] font-medium text-n-slate-12">
            {{ t(`CONVERSATION.HEADER.${copiaPorModo.status}`) }}
          </span>
        </div>
        <p
          v-if="showStatus && copiaPorModo.detalhe && !aiPaused"
          class="m-0 mt-2 text-[11px] leading-snug text-n-slate-10"
          data-testid="ai-mode-detail"
        >
          {{ t(`CONVERSATION.HEADER.${copiaPorModo.detalhe}`) }}
        </p>

        <button
          v-if="canControlAi"
          type="button"
          class="mt-3 flex h-8 w-full items-center justify-center gap-1.5 rounded-full text-[12.5px] font-bold disabled:opacity-50"
          :class="
            aiPaused
              ? 'bg-n-teal-9 text-white hover:bg-n-teal-10'
              : 'border border-n-weak text-n-slate-12 hover:bg-n-solid-2'
          "
          :disabled="saving"
          data-testid="ai-agent-toggle"
          @click="toggleAi"
        >
          <span
            :class="aiPaused ? 'i-ri-play-fill' : 'i-ri-pause-fill'"
            class="size-4"
            aria-hidden="true"
          />
          {{ t(`CONVERSATION.HEADER.${copiaPorModo.botao}`) }}
        </button>
        <p
          v-if="canControlAi"
          class="m-0 mt-2 text-[11px] leading-snug text-n-slate-10"
        >
          {{
            t(`CONVERSATION.HEADER.${copiaPorModo.dica}`, { name: activeName })
          }}
        </p>

        <!-- Troca de assistente saiu do popover (Lucas, 01/09): fica em Configurar assistente.
             Aqui so estado, pausa e follow-up. -->
        <div class="mt-3 border-t border-n-weak pt-2">
          <button
            v-if="showFollowup && !paused"
            type="button"
            class="flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-[13px] text-n-slate-12 hover:bg-n-solid-2 disabled:opacity-50"
            :disabled="saving"
            @click="setFollowupPaused(!followupPaused)"
          >
            <span
              class="i-ri-alarm-line size-3.5 shrink-0"
              :class="followupPaused ? 'text-n-amber-11' : 'text-n-teal-11'"
            />
            {{
              followupPaused
                ? t('CONVERSATION.HEADER.RESUME_FOLLOWUP')
                : t('CONVERSATION.HEADER.PAUSE_FOLLOWUP')
            }}
            <span v-if="followupCount" class="text-n-slate-10">
              {{
                t('CONVERSATION.HEADER.FOLLOWUP_COUNT', {
                  count: followupCount,
                })
              }}
            </span>
          </button>
        </div>

        <button
          type="button"
          class="mt-2.5 flex items-center gap-1.5 text-[12px] font-bold text-n-blue-11 hover:underline"
          @click="configureAssistant"
        >
          {{ t('CONVERSATION.HEADER.AI_CONFIGURE') }}
          <span class="i-ri-external-link-line size-3.5" aria-hidden="true" />
        </button>
        <template v-if="pinnable" #actions>
          <PinAction modulo="ai" @done="emit('close')" />
        </template>
      </ChipPopover>
      <button
        v-if="canControlAi && !rail"
        type="button"
        data-testid="ai-agent-pause-shortcut"
        class="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 hover:bg-n-alpha-3 disabled:opacity-50"
        :class="aiPaused ? 'text-n-amber-11' : 'text-n-teal-11'"
        :disabled="saving"
        :aria-pressed="aiPaused"
        :aria-label="
          aiPaused
            ? t('CONVERSATION.HEADER.RESUME_AI_SHORTCUT')
            : t('CONVERSATION.HEADER.PAUSE_AI_SHORTCUT')
        "
        :title="
          aiPaused
            ? t('CONVERSATION.HEADER.RESUME_AI_SHORTCUT')
            : t('CONVERSATION.HEADER.PAUSE_AI_SHORTCUT')
        "
        @click.stop="toggleAi"
      >
        <span
          class="size-4 shrink-0"
          :class="aiPaused ? 'i-ri-play-fill' : 'i-ri-pause-fill'"
          aria-hidden="true"
        />
      </button>
    </div>
  </div>
</template>
